import React, { useEffect, useState, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Navbar from '../../../../components/navbar';
import Footer from '../../../../components/footer';
import Table from '../../../../components/Table';
import Pagination from '../../../../components/Pagination';
import ReadOnlyInput from '../../../../components/ReadOnlyInput';
import PatientSignatureModal from '../../../global/view/patientSignature';
import DoctorePrescription from '../../../global/view/doctorePrescription';
import ClaimStatus from '../../../global/view/ClaimStatus';
import ClaimApprovedTable from '../../../global/view/ClaimApprovedTable';
import PharmacistSignatureModal from '../../../global/view/PharmacistSignatureModal';
import ActionTile from '../../Chronic/components/ActionTile';
import FeeSummaryCard from '../../Chronic/components/FeeSummaryCard';
import DeliveryOptions from '../../Chronic/components/DeliveryOptions';
import styles from '../../Chronic/css/ChronicScriptView.module.css';
import { saveRepeatDrugs, signRepeat } from '../../../../api/dispensary/script/processScriptApi';

import { useAuth } from '../../../../context/AuthContext';
import { useCompanyAndDispensingFee } from '../../Chronic/utils/useCompanyAndDispensingFee';
import { useScriptPatientProfile } from '../../Chronic/utils/useScriptPatientProfile';
import { useScriptTableData } from '../../Chronic/utils/useScriptTableData';
import { buildPrescription, calculatePricing } from '../../Chronic/utils/pricingUtils';

const PackageScriptView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { scriptData, continueAfterMedicalAid } = location.state || {};
    const baseState = location.state || {};

    const [showPatientSig, setShowPatientSig] = useState(false);
    const [pharmacistSig, setPharmacistSig] = useState(null);
    const [patientSig, setPatientSig] = useState(null);
    const [showDoctorModal, setShowDoctorModal] = useState(false);
    const [showPharmacistSig, setShowPharmacistSig] = useState(false);
    const [showClaimStatus, setShowClaimStatus] = useState(false);
    const [showClaimApproved, setShowClaimApproved] = useState(false);
    const [progressPercent, setProgressPercent] = useState(0);
    const [mode, setMode] = useState('Delivery');
    const rowsPerPage = 4;

    const { companyName, pharmacyRegNumber, dispensingFee } = useCompanyAndDispensingFee(user?.branchcode);
    const patientProfile = useScriptPatientProfile(scriptData);

    const todayIsoDate = useMemo(() => {
        try {
            return new Date().toISOString().split('T')[0];
        } catch {
            return undefined;
        }
    }, []);

    const {
        tableData,
        currentPage,
        setCurrentPage,
        pagedData,
        totalPages,
    } = useScriptTableData(scriptData, rowsPerPage);

    const isMedicalAid = (scriptData?.paymentType || '').toLowerCase() === 'medical aid';

    // On landing, if Medical Aid, immediately show ClaimStatus (medical-aid1.png flow)
    useEffect(() => {
        if (isMedicalAid && !continueAfterMedicalAid) {
            setProgressPercent(0);
            setShowClaimStatus(true);
        }
    }, [isMedicalAid, continueAfterMedicalAid]);

    useEffect(() => {
        if (continueAfterMedicalAid) {
            if (location.state?.pharmacistSignature) {
                setPharmacistSig(location.state.pharmacistSignature);
            }
            setShowClaimStatus(false);
            setShowPatientSig(true);
        }
    }, [continueAfterMedicalAid, location.state]);

    if (!scriptData) {
        return <div>Loading...</div>;
    }

    const rawScriptNumber = (() => {
        const navType = baseState.type || scriptData.type;

        // If processing a script, prefer the process script number explicitly
        if (navType === 'process') {
            return (
                baseState.processscriptnumber ??
                scriptData.processscriptnumber ??
                scriptData.processScriptNumber ??
                baseState.scriptNo ??
                scriptData.scriptNo ??
                scriptData.scriptNumber ??
                baseState.scriptNumber ??
                ''
            );
        }

        // If clinic flow, prefer clinic reference explicitly
        if (navType === 'clinic') {
            return (
                baseState.clinicreference ??
                scriptData.clinicreference ??
                scriptData.clinicReference ??
                ''
            );
        }

        // Fallback: previous heuristic
        return (
            baseState.scriptNumber ??
            baseState.processscriptnumber ??
            baseState.scriptNo ??
            scriptData.processscriptnumber ??
            scriptData.processScriptNumber ??
            scriptData.scriptNo ??
            scriptData.scriptNumber ??
            ''
        );
    })();

    const scriptNumberDisplay = rawScriptNumber != null ? String(rawScriptNumber) : '';

    const patientLabel = scriptData?.patient || '';
    const profileLabelFromData = scriptData?.profileNo || '';
    const profileFromLabelMatch = /\(([^()]+)\)\s*$/.exec(patientLabel || '');
    const profileLabel = profileLabelFromData || (profileFromLabelMatch ? profileFromLabelMatch[1] : '');

    const scriptAddressLines = scriptData?.addressLines || [];
    const profileAddressLines =
        patientProfile && Array.isArray(patientProfile.addressLines)
            ? patientProfile.addressLines
            : [];

    const scriptPhone = scriptData?.phone || '';
    const profilePhone = (patientProfile?.phone && String(patientProfile.phone).trim()) || '';

    const patientInfo = {
        name: patientLabel || 'Patient',
        patient: patientLabel || '',
        profile: profileLabel || (patientProfile?.profileNo || ''),
        address:
            (Array.isArray(scriptAddressLines) && scriptAddressLines.length
                ? scriptAddressLines
                : profileAddressLines),
        phone: scriptPhone || profilePhone || '',
        medicalAid:
            (patientProfile?.medicalAid && String(patientProfile.medicalAid).trim())
                || scriptData?.medicalAidNumber
                || '',
    };

    // Gross: apply PR / PR26 pricing rules from original cashpricingoption
    // (mirrors ScriptHistoryDetails), then add dispensing fee.
    const { dispensingFeeValue, grossWithDispensing, grossAmount } = calculatePricing(scriptData, dispensingFee);

    const prescription = buildPrescription(
        scriptData,
        companyName,
        pharmacyRegNumber,
        grossAmount,
    );

    // Debug logging to inspect what the preview modal receives
    console.log('[PackageScriptView] preview data', {
        scriptData,
        patientProfile,
        patientInfo,
        prescription,
    });

    const repeatId = baseState.repeatId || scriptData?.repeatId || null;
    const repeatNumber = baseState.repeatNumber ?? scriptData?.repeatNumber ?? null;

    const handleViewScriptDocument = () => {
        const url =
            scriptData?.scriptDocUrl ||
            scriptData?.docurl ||
            '';

        if (url && typeof window !== 'undefined') {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            console.log('[PackageScriptView] No script document URL available for this prescription');
        }
    };

    const handlePackageAndLabel = async () => {
        if (!repeatId) {
            return;
        }
        const items = (scriptData?.drugs || [])
            .map((d) => ({
                prescriptionDrugId: d.prescriptionDrugId,
                quantity: d.quantity ?? d.qty ?? null,
                status: 'Dispensed',
            }))
            .filter((it) => it.prescriptionDrugId);

        if (!items.length) {
            return;
        }

        try {
            await saveRepeatDrugs({ repeatid: repeatId, items });
        } catch (e) {
            console.error('Failed to save repeat drugs', e);
        }
    };

    const handleMedicationLabel = () => {
        console.log('[PackageScriptView] ========== MEDICATION LABEL CLICKED ==========');
        console.log('[PackageScriptView] scriptData:', scriptData);
        console.log('[PackageScriptView] baseState:', baseState);
        console.log('[PackageScriptView] rawScriptNumber:', rawScriptNumber);
        console.log('[PackageScriptView] scriptNumberDisplay:', scriptNumberDisplay);
        
        if (!scriptData) {
            console.log('[PackageScriptView] ERROR: No script data available');
            return;
        }

        console.log('[PackageScriptView] handleMedicationLabel called');
        console.log('[PackageScriptView] Gross with dispensing:', grossWithDispensing);
        console.log('[PackageScriptView] Gross amount:', grossAmount);

        // Extract ICD-10 codes from drugs
        const icd10Codes = (scriptData.drugs || []).map(drug => drug.icd10).filter(Boolean);
        console.log('[PackageScriptView] Extracted ICD-10 codes:', icd10Codes);
        
        // Create label data structure
        const labelData = (scriptData.drugs || []).map((drug, index) => ({
            id: uuidv4(),
            data: {
                patientName: scriptData.patient || "",
                medication: drug.name || "",
                quantity: drug.quantity || drug.qty || "",
                prescriber: scriptData.pharmacist || "",
                rxNumber: scriptNumberDisplay || "",
                directions: drug.instructions || "",
                packSize: drug.packSize || "",
                icd10: drug.icd10 || "",
                practitioner: scriptData.practitioner || "",
                barcode: drug.barcode || "",
                date: new Date().toISOString().split('T')[0],
            },
            isDrug: true,
        }));

        console.log('[PackageScriptView] Created label data:', labelData);

        // Create medications array for dropdown
        const medicationsArray = (scriptData.drugs || []).map((drug, index) => ({
            id: uuidv4(),
            name: drug.name || "",
            strength: drug.strength || "",
            dosageForm: drug.dosageForm || "",
            schedule: drug.schedule || "",
            packSize: drug.packSize || "",
            sku: drug.sku || "",
        }));

        console.log('[PackageScriptView] Created medications array:', medicationsArray);

        // Navigate to label configuration page with data
        // Use scriptNumberDisplay if available, otherwise try to get prescription ID from scriptData
        console.log('[PackageScriptView] Checking all possible prescription ID sources:');
        console.log('- scriptNumberDisplay:', scriptNumberDisplay);
        console.log('- baseState?.prescriptionid:', baseState?.prescriptionid);
        console.log('- baseState?.prescriptionId:', baseState?.prescriptionId);
        console.log('- scriptData?.prescriptionid:', scriptData?.prescriptionid);
        console.log('- scriptData?.prescriptionId:', scriptData?.prescriptionId);
        console.log('- scriptData?.id:', scriptData?.id);
        console.log('- scriptData?.prescriptionID:', scriptData?.prescriptionID);
        console.log('- repeatId:', repeatId);
        
        const prescriptionId = baseState?.prescriptionId ||
                              baseState?.prescriptionid ||
                              scriptData?.prescriptionId || 
                              scriptData?.prescriptionid || 
                              scriptData?.id ||
                              scriptData?.prescriptionID ||
                              scriptNumberDisplay ||
                              '';
                              
        console.log('[PackageScriptView] Final prescriptionId for URL:', prescriptionId);
        
const targetUrl = prescriptionId
? `/dispensary/label-configuration?prescriptionId=${prescriptionId}&icd10Codes=${icd10Codes.join(',')}`
: `/dispensary/label-configuration?icd10Codes=${icd10Codes.join(',')}`;

const navigationState = {
labelData: labelData,
medications: medicationsArray,
scriptNumber: scriptNumberDisplay || "",
pharmacyInfo: {
name: companyName,
address: scriptData.addressLines || [],
phone: scriptData.phone || "",
},
totals: {
subtotal: scriptData.subtotal,
vat: scriptData.vat,
total: scriptData.total,
dispensingFee: dispensingFeeValue,
gross: grossWithDispensing,
},
baseState: { ...baseState, repeatId, repeatNumber }, // Add baseState to pass repeat information
scriptData: { ...scriptData, repeatId, repeatNumber }, // Add scriptData as well
isFromDispensaryMenu: true,
};
                        console.log('[PackageScriptView] Navigation state:', navigationState);
        navigate(targetUrl, { state: navigationState });
    };

    return (
        <div className={styles.page}>
            <Navbar backTo="/customer/dispensary/package-script" />
            <main className={styles.main}>
                {showClaimApproved ? (
                    <ClaimApprovedTable
                        scriptData={scriptData}
                        onInPlaceContinue={(sig) => {
                            setPharmacistSig(sig);
                            setShowClaimApproved(false);
                            setShowPatientSig(true);
                        }}
                    />
                ) : (
                    <>
                        {/* Top controls */}
                        <div className={styles.controlsRow}>
                            <ReadOnlyInput label="Script Number" value={scriptNumberDisplay} />

                            <ReadOnlyInput label="Total Items" value={scriptData.totalItems ?? tableData.length} />
                            <ReadOnlyInput label="Pharmacist" value={scriptData.pharmacist || 'N Khan'} />
                            <ReadOnlyInput label="Payment Type" value={scriptData.paymentType || 'Cash'} />
                            <ReadOnlyInput label="Practitioner" value={scriptData.practitioner || 'M Moodley - 689523'} />
                        </div>

                        {/* Table */}
                        <div className={styles.tableCard}>
                            <Table
                                columns={[
                                    { header: 'Drug Name', accessor: 'drugName' },
                                    { header: 'Pack Size', accessor: 'packSize' },
                                    { header: 'ICD-10', accessor: 'icd10' },
                                    { header: 'Strength', accessor: 'strength' },
                                    { header: 'Qty', accessor: 'qty' },
                                    { header: 'Nappi Code', accessor: 'nappiCode' },
                                    { header: 'Instructions', accessor: 'instructions' },
                                    { header: 'Cost Price', accessor: 'costPrice' },
                                ]}
                                data={pagedData}
                            />
                        </div>

                        {/* Actions left; delivery middle; fees right */}
                        <div className={styles.midRow}>
                            <div className={styles.tilesMiddle}>
                                <div className={styles.tilesCol}>
                                    <ActionTile title="Script" rightLinkLabel="View" onRightLinkClick={handleViewScriptDocument} />

                                    <ActionTile title="Sign Invoice" onClick={() => setShowPharmacistSig(true)} />
                                </div>
                            </div>
                            <div className={styles.optionsRight}>
                                <DeliveryOptions
                                    mode={mode}
                                    onModeChange={setMode}
                                    minDate={todayIsoDate}
                                />
                            </div>
                            <div className={styles.feesLeft}>
                                <FeeSummaryCard
                                    dispensingFee={
                                        Number.isFinite(dispensingFeeValue) && dispensingFeeValue > 0
                                            ? `R ${dispensingFeeValue.toFixed(2)}`
                                            : (scriptData.dispensingFee || 'R0.00')
                                    }
                                    gross={
                                        grossAmount
                                            ? `R ${grossAmount}`
                                            : (scriptData.gross || 'R219.50')
                                    }
                                />
                            </div>
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />

                        {/* Bottom buttons */}
                        <div className={styles.bottomButtons}>
                            <button className={styles.btnPrimary} onClick={handleMedicationLabel}>
                                Medication Label
                            </button>
                        </div>
                    </>
                )}
            </main>
            <Footer />

            <PatientSignatureModal
                isOpen={showPatientSig}
                onClose={() => setShowPatientSig(false)}
                onConfirm={async (dataUrl) => {
                    setPatientSig(dataUrl);

                    if (repeatId) {
                        const items = (scriptData?.drugs || [])
                            .map((d) => ({
                                prescriptionDrugId: d.prescriptionDrugId,
                                quantity: d.quantity ?? d.qty ?? null,
                                status: 'Dispensed',
                            }))
                            .filter((it) => it.prescriptionDrugId);

                        if (items.length) {
                            try {
                                await saveRepeatDrugs({ repeatid: repeatId, items });
                            } catch (e) {
                                console.error('Failed to save repeat drugs on patient sign', e);
                            }
                        }

                        try {
                            await signRepeat(repeatId, {
                                paymenttype: scriptData?.paymentType || null,
                                gross: grossWithDispensing || null,
                                dispensingfee: dispensingFeeValue || null,
                                deliveryoption: mode || null,
                            });
                        } catch (e) {
                            console.error('Failed to sign repeat on patient sign', e);
                        }
                    }

                    setShowPatientSig(false);
                    setShowDoctorModal(true);
                }}
                initialSignature={patientSig}
                totals={{
                    amount: scriptData?.amount ?? 'R129.18',
                    vat: scriptData?.vat ?? 'R19.38',
                    total: scriptData?.total
                        ? `R${scriptData.total.toFixed?.(2) ?? scriptData.total}`
                        : 'R148.56',
                }}
            />

            <PharmacistSignatureModal
                isOpen={showPharmacistSig}
                onClose={() => setShowPharmacistSig(false)}
                onNext={(sig) => {
                    setPharmacistSig(sig);
                    setShowPharmacistSig(false);
                    if (isMedicalAid) {
                        setProgressPercent(0);
                        setShowClaimStatus(true);
                    } else {
                        setShowPatientSig(true);
                    }
                }}
                previewProps={{ patientInfo, prescription }}
            />

            <DoctorePrescription
                isOpen={showDoctorModal}
                onClose={() => setShowDoctorModal(false)}
                patientInfo={patientInfo}
                prescription={prescription}
                pharmacistSignature={pharmacistSig}
                patientSignature={patientSig}
                totals={{
                    vat: scriptData?.vat ?? 'R19.38',
                    total: scriptData?.total
                        ? `R${scriptData.total.toFixed?.(2) ?? scriptData.total}`
                        : 'R148.56',
                }}
                useCompletedPopup={true}
            />

            {isMedicalAid && (
                <ClaimStatus
                    isOpen={showClaimStatus}
                    onClose={() => setShowClaimStatus(false)}
                    scriptData={scriptData}
                    claimDetails={{ progressPercent }}
                />
            )}
        </div>
    );
};

export default PackageScriptView;